// src/hooks/useHistoryNavigation.ts (COMPLETE FIX FOR NAVIGATION)
import { useCallback } from "react";
import { MoveHistoryManager } from "../utils/MoveHistoryManager.ts";
import { getSetupByName } from "../utils/boardSetup.ts";

interface UseHistoryNavigationProps {
  historyManager: MoveHistoryManager;
  currentGameId: string;
  gamePresets: any[];
  historyPosition: number;
  setHistoryPosition: (pos: number) => void;
  setIsReviewingHistory: (reviewing: boolean) => void;
  setGamePieces: (pieces: Map<string, any>) => void;
  setCapturedPieces: (pieces: any[]) => void;
  setCurrentTurn: (turn: "white" | "black") => void;
  initialPieces?: Map<string, any>;  // ⭐ NEW: Initial board state for navigation
}

export const useHistoryNavigation = ({
  historyManager,
  currentGameId,
  gamePresets,
  historyPosition,
  setHistoryPosition,
  setIsReviewingHistory,
  setGamePieces,
  setCapturedPieces,
  setCurrentTurn,
  initialPieces,  // ⭐ NEW: Extract initialPieces parameter
}: UseHistoryNavigationProps) => {
  
  const goBackward = useCallback(() => {
    console.log("◀️ goBackward called, current position:", historyPosition);
    
    if (historyPosition < 0) {
      console.log("❌ Already at initial position");
      return;
    }

    if (historyPosition === 0) {
      // Going back to initial position
      console.log("📍 Navigating back to initial position");
      
      // ⭐ USE PROVIDED INITIAL PIECES IF AVAILABLE
      if (initialPieces && initialPieces.size > 0) {
        console.log("✅ Using saved initial position:", initialPieces.size, "pieces");
        setGamePieces(new Map(initialPieces));
      } else {
        console.log("⚠️ Using preset initial position");
        const preset = gamePresets.find((p) => p.id === currentGameId);
        if (preset) {
          const presetPieces = getSetupByName(preset.setup, preset.rows);
          setGamePieces(presetPieces);
        }
      }
      
      setCapturedPieces([]);
      setCurrentTurn("white");
      setHistoryPosition(-1);
      setIsReviewingHistory(true);
      console.log("✅ Moved to initial position (position -1)");
      return;
    }

    // Replay moves up to previous position
    console.log("📜 Replaying moves up to position:", historyPosition - 1);
    const moves = historyManager.getMoves();
    const targetPosition = historyPosition - 1;

    // Start from initial position
    let currentPieces: Map<string, any>;
    
    // ⭐ USE PROVIDED INITIAL PIECES IF AVAILABLE
    if (initialPieces && initialPieces.size > 0) {
      console.log("✅ Starting replay from saved initial position");
      currentPieces = new Map(initialPieces);
    } else {
      console.log("⚠️ Starting replay from preset initial position");
      const preset = gamePresets.find((p) => p.id === currentGameId);
      if (!preset) return;
      currentPieces = getSetupByName(preset.setup, preset.rows);
    }

    const capturedPiecesAtPosition: any[] = [];

    // Replay moves up to target position
    for (let i = 0; i <= targetPosition; i++) {
      const move = moves[i];
      const fromKey = `${move.from.row}-${move.from.col}`;
      const toKey = `${move.to.row}-${move.to.col}`;

      const piece = currentPieces.get(fromKey);
      if (piece) {
        const targetPiece = currentPieces.get(toKey);
        if (targetPiece) {
          capturedPiecesAtPosition.push(targetPiece);
        }

        currentPieces.delete(fromKey);
        currentPieces.set(toKey, {
          ...piece,
          position: move.to,
          hasMoved: true,
        });
      }
    }

    setGamePieces(currentPieces);
    setCapturedPieces(capturedPiecesAtPosition);
    setCurrentTurn(moves[targetPosition].pieceColor === "white" ? "black" : "white");
    setHistoryPosition(targetPosition);
    setIsReviewingHistory(true);
    console.log("✅ Moved to position:", targetPosition);
  }, [
    historyPosition,
    historyManager,
    gamePresets,
    currentGameId,
    initialPieces,  // ⭐ NEW: Add to dependencies
    setGamePieces,
    setCapturedPieces,
    setCurrentTurn,
    setHistoryPosition,
    setIsReviewingHistory,
  ]);

  const goForward = useCallback(() => {
    console.log("▶️ goForward called, current position:", historyPosition);
    
    const moves = historyManager.getMoves();
    if (historyPosition >= moves.length - 1) {
      console.log("❌ Already at final position");
      return;
    }

    const nextPosition = historyPosition + 1;
    console.log("📍 Moving forward to position:", nextPosition);

    // If at initial position, start from initial pieces
    let currentPieces: Map<string, any>;
    
    if (historyPosition === -1) {
      // ⭐ USE PROVIDED INITIAL PIECES IF AVAILABLE
      if (initialPieces && initialPieces.size > 0) {
        console.log("✅ Starting forward from saved initial position:", initialPieces.size, "pieces");
        currentPieces = new Map(initialPieces);
      } else {
        console.log("⚠️ Starting forward from preset initial position");
        const preset = gamePresets.find((p) => p.id === currentGameId);
        if (!preset) {
          console.error("❌ No preset found for:", currentGameId);
          return;
        }
        currentPieces = getSetupByName(preset.setup, preset.rows);
      }
    } else {
      // Replay all moves up to current position
      console.log("📜 Replaying moves from start to position:", historyPosition);
      
      // ⭐ START FROM INITIAL PIECES
      if (initialPieces && initialPieces.size > 0) {
        currentPieces = new Map(initialPieces);
      } else {
        const preset = gamePresets.find((p) => p.id === currentGameId);
        if (!preset) return;
        currentPieces = getSetupByName(preset.setup, preset.rows);
      }

      for (let i = 0; i <= historyPosition; i++) {
        const move = moves[i];
        const fromKey = `${move.from.row}-${move.from.col}`;
        const toKey = `${move.to.row}-${move.to.col}`;

        const piece = currentPieces.get(fromKey);
        if (piece) {
          currentPieces.delete(fromKey);
          currentPieces.set(toKey, {
            ...piece,
            position: move.to,
            hasMoved: true,
          });
        }
      }
    }

    // Apply the next move
    const nextMove = moves[nextPosition];
    console.log("🎯 Applying move:", nextPosition, nextMove.notation);
    
    const fromKey = `${nextMove.from.row}-${nextMove.from.col}`;
    const toKey = `${nextMove.to.row}-${nextMove.to.col}`;

    const capturedPiecesAtPosition: any[] = [];

    // Recalculate captured pieces up to next position
    const replayPieces = initialPieces && initialPieces.size > 0
      ? new Map(initialPieces)
      : getSetupByName(
          gamePresets.find((p) => p.id === currentGameId)?.setup || "traditional",
          gamePresets.find((p) => p.id === currentGameId)?.rows || 8
        );

    for (let i = 0; i <= nextPosition; i++) {
      const move = moves[i];
      const mFromKey = `${move.from.row}-${move.from.col}`;
      const mToKey = `${move.to.row}-${move.to.col}`;

      const piece = replayPieces.get(mFromKey);
      if (piece) {
        const targetPiece = replayPieces.get(mToKey);
        if (targetPiece) {
          capturedPiecesAtPosition.push(targetPiece);
        }

        replayPieces.delete(mFromKey);
        replayPieces.set(mToKey, {
          ...piece,
          position: move.to,
          hasMoved: true,
        });
      }
    }

    const piece = currentPieces.get(fromKey);
    if (piece) {
      currentPieces.delete(fromKey);
      currentPieces.set(toKey, {
        ...piece,
        position: nextMove.to,
        hasMoved: true,
      });
    }

    setGamePieces(currentPieces);
    setCapturedPieces(capturedPiecesAtPosition);
    setCurrentTurn(nextMove.pieceColor === "white" ? "black" : "white");
    setHistoryPosition(nextPosition);
    setIsReviewingHistory(true);
    console.log("✅ Moved to position:", nextPosition, "| Pieces on board:", currentPieces.size);
  }, [
    historyPosition,
    historyManager,
    gamePresets,
    currentGameId,
    initialPieces,  // ⭐ NEW: Add to dependencies
    setGamePieces,
    setCapturedPieces,
    setCurrentTurn,
    setHistoryPosition,
    setIsReviewingHistory,
  ]);

  const goToCurrentPosition = useCallback(() => {
    console.log("⏩ goToCurrentPosition called");
    
    const moves = historyManager.getMoves();
    const finalPosition = moves.length - 1;

    if (finalPosition < 0) {
      console.log("❌ No moves to navigate to");
      return;
    }

    console.log("📍 Jumping to final position:", finalPosition);

    // Replay all moves from initial position
    let currentPieces: Map<string, any>;
    
    // ⭐ START FROM INITIAL PIECES
    if (initialPieces && initialPieces.size > 0) {
      console.log("✅ Replaying from saved initial position");
      currentPieces = new Map(initialPieces);
    } else {
      console.log("⚠️ Replaying from preset initial position");
      const preset = gamePresets.find((p) => p.id === currentGameId);
      if (!preset) return;
      currentPieces = getSetupByName(preset.setup, preset.rows);
    }

    const capturedPiecesAtPosition: any[] = [];

    for (let i = 0; i <= finalPosition; i++) {
      const move = moves[i];
      const fromKey = `${move.from.row}-${move.from.col}`;
      const toKey = `${move.to.row}-${move.to.col}`;

      const piece = currentPieces.get(fromKey);
      if (piece) {
        const targetPiece = currentPieces.get(toKey);
        if (targetPiece) {
          capturedPiecesAtPosition.push(targetPiece);
        }

        currentPieces.delete(fromKey);
        currentPieces.set(toKey, {
          ...piece,
          position: move.to,
          hasMoved: true,
        });
      }
    }

    setGamePieces(currentPieces);
    setCapturedPieces(capturedPiecesAtPosition);
    setCurrentTurn(moves[finalPosition].pieceColor === "white" ? "black" : "white");
    setHistoryPosition(finalPosition);
    setIsReviewingHistory(false);
    console.log("✅ Jumped to current position:", finalPosition);
  }, [
    historyManager,
    gamePresets,
    currentGameId,
    initialPieces,  // ⭐ NEW: Add to dependencies
    setGamePieces,
    setCapturedPieces,
    setCurrentTurn,
    setHistoryPosition,
    setIsReviewingHistory,
  ]);

  return {
    goBackward,
    goForward,
    goToCurrentPosition,
  };
};
